import { Autocomplete, Box, Button, Divider, Flex, Group, LoadingOverlay, Modal, Select, SimpleGrid, Space, Text, Textarea, TextInput } from '@mantine/core';
import React, { memo, useContext, useEffect, useState } from 'react';
import { authContext } from '../../../../../context/AuthProvider.component';
import { notifications } from '@mantine/notifications';
import { baseUrl } from '../../../../../Constants/constant';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';

interface UtilityProps {
    // onComplete: () => void;
    onBack: () => void;
    opendrawer: any;
    setDrawerState: (state: boolean) => void;
}
interface UtilityPayload {
    compute_id: string;
    customer_id: string | null;       
    is_draft: number;
    step: string;
    file_name: string | null;
}

const Utility = ({ onBack, setDrawerState, opendrawer }: UtilityProps) => {
    const [opened, { open, close }] = useDisclosure(false);
    const navigate = useNavigate();
    const [reportComment, setReportComment] = useState<string>('');
    const [editId, setEditId] = useState<number | null>();
    const [error, setErrors] = useState<Record<string, string>>({});
    const [fileName, setFileName] = useState<string>('');
    const [payloads, setPayloads] = useState<UtilityPayload | null>(null);
    const [saveMode, setSaveMode] = useState<'next' | 'draft' | 'skip'>('next');
    const [customerdata, setCustomerData] = useState<any>();
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
    const [selectedCustomerLabel, setSelectedCustomerLabel] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { computeId, draftLca, setDraftLca } = useContext<any>(authContext);
    const token = localStorage.getItem("token") || "";

    // State for API data
    const [getCustomerData, setGetCustomerData] = useState<any>(null);
    const [getUtilityData, setGetUtilityData] = useState<any>(null);
    const [utilityLoading, setUtilityLoading] = useState(false);

    // Fetch customer data function
    const fetchCustomerData = async (url: string) => {
        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    token: token,
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const result = await response.json();
            setGetCustomerData(result);
        } catch (error) {
            console.error('Error fetching customer data:', error);
            showNotification('error', 'Error loading customer data');
        }
    };

    // Fetch utility data function
    const fetchUtilityData = async (url: string) => {
        try {
            setUtilityLoading(true);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "token": token,
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const result = await response.json();
            setGetUtilityData(result);
        } catch (error) {
            console.error('Error fetching utility data:', error);
            showNotification('error', 'Error loading utility data');
        } finally {
            setUtilityLoading(false);
        }
    };

    useEffect(() => {
        if (computeId) {
            fetchUtilityData(`${baseUrl}compute/lca/${computeId}?is_draft=${draftLca}`);
            fetchCustomerData(`${baseUrl}admin/customers`)
        }
    }, [computeId]);

    useEffect(() => {
        if (getCustomerData?.data?.rows) {
            setCustomerData(
                getCustomerData?.data?.rows?.map((el: any) => ({
                    value: el?.id.toString(), // this is customer_id
                    label: el?.customer_name,
                })));
            // setCustomerData(customerOptions);    
        }
    }, [getCustomerData])

useEffect(() => {
    if (getUtilityData?.data?.rows?.length > 0) {
        const computeJourneys = getUtilityData?.data?.rows[0]?.ComputeCommentLogo;
        setEditId(computeJourneys?.id)
        if (computeJourneys?.customer_id) {
            setSelectedCustomerId(computeJourneys?.customer_id?.toString());
            
            // Find and set the corresponding label
            const customerLabel = customerdata?.find((item: any) => 
                item.value === computeJourneys?.customer_id?.toString()
            )?.label;
            setSelectedCustomerLabel(customerLabel || null);
        }
        if (computeJourneys?.report_comment) {
            setReportComment(computeJourneys?.report_comment);
        }
    }
}, [getUtilityData, customerdata]);


    const showNotification = (type: 'success' | 'error', message: string) => {
        notifications.show({
            title: type === 'success' ? 'Success' : 'Error',
            message,
            color: type === 'success' ? 'green' : 'red',
        });
    };

    const handleChange = (field: string, value: string | null) => {
        if (field === "file_name") {
            setFileName(value || '');
        }
        if (field === "report_comment") {
            setReportComment(value || '');
        }
        if (field === "customer_name") {
            setSelectedCustomerLabel(value); // update label for display

            const selected = customerdata?.find((item: any) => item.label === value);
            setSelectedCustomerId(selected?.value ?? null); // update ID for API
        }

        setErrors((prev: any) => ({ ...prev, [field]: undefined }));
    };

    const validateFields = () => {
        let newErrors: Record<string, string> = {};

        if (!selectedCustomerId) {
            newErrors.customer_id = "Customer icon is Required";
        }
        if (saveMode === 'draft' && (!fileName || fileName.trim() === '')) {
            newErrors.file_name = "File name is Required";
        } else if (fileName && (fileName.length < 3 || fileName.length > 50)) {
            newErrors.file_name = "File name must be between 3 and 50 characters";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }
        return true;
    };

    const handleSave = async (e: any, mode: 'next' | 'draft' | 'skip') => {
        e.preventDefault();
        if (mode === 'skip') {
            opendrawer();
            setDrawerState(true);
            return;
        }
        setSaveMode(mode);
        mode === 'next' ? setDraftLca(0) : setDraftLca(1);
        if (!validateFields()) {
            showNotification('error', 'Please fill all required fields');
            return;
        }
        const newPayload = {
            compute_id: computeId,
            customer_id: selectedCustomerId,
            report_comment: reportComment,
            is_draft: mode === 'draft' ? 1 : 0,
            step: "Comments",
            ...(editId && { id: editId }),
            file_name: fileName || null,
        };
        setPayloads(newPayload);

    };

    useEffect(() => {
        const makeApiCall = async () => {
            if (payloads) {
                const url = editId
                    ? `${baseUrl}compute/lca/updateCustomerLogo`
                    : `${baseUrl}compute/lca/addCustomerLogo`;

                try {
                    setIsLoading(true);
                    
                    const response = await fetch(url, {
                        method: editId ? 'PUT' : 'POST',
                        headers: {
                            "Content-Type": "application/json",
                            "Access-Control-Allow-Origin": "*",
                            token: token,
                        },
                        body: JSON.stringify(payloads)
                    });
                    
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    
                    const result = await response.json();
                    
                    // Handle the response immediately after getting it
                    if (saveMode === 'draft') {
                        // Close modal first
                        close();
                        
                        // Show success notification
                        showNotification('success', 'File Saved in Draft Successfully');
                        
                        // Navigate to draft page
                        navigate('/draft');
                    } else {
                        // For next mode, call opendrawer and setDrawerState immediately
                        opendrawer();
                        setDrawerState(true);
                        showNotification('success', result?.message || 'Comments/Assumptions Saved Successfully');
                    }
                    
                    // Reset payloads to prevent duplicate processing
                    setPayloads(null);
                    
                } catch (error) {
                    console.error('API Error:', error);
                    showNotification('error', 'An error occurred while saving');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        makeApiCall();
    }, [payloads, editId, token, saveMode, close, navigate, opendrawer, setDrawerState]);

    return (
        <div>
            <LoadingOverlay
                visible={utilityLoading}
                zIndex={1000}
                overlayProps={{
                    radius: 'sm',
                    blur: 2,
                    style: {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }
                }}
                loaderProps={{ color: '#404040', type: 'bars' }}
            />
            <Box>
                <Text size="xl" pb={5} fw={500}>Customer Logo & Comments</Text>
                <SimpleGrid cols={3}>
                    <Autocomplete
                        label="Select Customer"
                        variant="filled"
                        radius="md"
                        withAsterisk
                        data={customerdata}
                        value={selectedCustomerLabel ?? undefined}
                        onChange={(value) => handleChange("customer_name", value)}
                        placeholder="Search by Customer name"
                        error={error?.["customer_name"]}
                    />
                </SimpleGrid>
                <Space h={'md'} />
                <Textarea
                    label="Comments"
                    placeholder="Enter your Comments"
                    withAsterisk
                    variant="filled"
                    autosize
                    value={reportComment}
                    onChange={(e) => handleChange("report_comment", e.target.value)}
                    minRows={2}
                    maxRows={4}
                    radius={6}
                    error={error?.["report_comment"]}
                />
                <Space h={'xl'} />
            </Box>
            <Modal centered opened={opened} onClose={close} title="Save in Draft">
                <TextInput
                    autoFocus
                    label="File Name"
                    value={fileName}
                    onChange={(e) => handleChange("file_name", e.target.value)}
                    variant="filled"
                    withAsterisk
                    mb="lg"
                    placeholder="Please enter file name"
                    radius={6}
                    error={error?.file_name}
                />
                <Divider h={'md'} />
                <Flex justify="flex-end">
                    <Button
                        variant="gradient"
                        gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                        radius={6}
                        onClick={(e) => handleSave(e, 'draft')}
                        size="md"
                    >
                        Save
                    </Button>
                </Flex>

            </Modal>
            <Flex justify="flex-end">
                <Group>
                    <Button variant="default" radius={6} size="md" onClick={open}>
                        Save to Draft
                    </Button>
                    <Button variant="default" radius={6} size="md" onClick={onBack}>
                        Back
                    </Button>
                    <Button
                        variant="default"
                        radius={6}
                        onClick={(e) => handleSave(e, 'skip')}
                        size="md"
                    >
                        Skip
                    </Button>
                    <Button
                        variant="gradient"
                        gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                        radius={6}
                        onClick={(e) => handleSave(e, 'next')}
                        loading={isLoading}
                        disabled={isLoading}
                        size="md"
                    >
                        Next
                    </Button>
                </Group>
            </Flex>
        </div>
    );
};

export default memo(Utility);